import React, { useState, useRef } from "react";
import { of, switchMap, tap, catchError, Subject, takeUntil } from "rxjs";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import TimeSince from "./TimeSince";
import LikeButton from "../Publications/components/SocialFeatures/LikeButton";
import CommentForm from "./CommentForm";
import Comment from "../../models/Comment";
import apiClient from "../../services/apiClient";
import AddToast from "../../state/actions/AddToast";
import Linkify from "../atoms/Linkify";
import ImperativeTooltip from "../atoms/Tooltip/ImperativeTooltip";
import isPortal from "../../common/helpers/isPortal";
import isFullNamePublic from "../../common/helpers/isUserProfilePublic";
import isUserLoggedIn from "../../common/helpers/isUserLoggedIn";
import SourceEntity from "../Publications/models/SourceEntity";
import conf from "./linkifyConf";
import Btn from "../atoms/Btn";
import store from "../../state/store";
import Topic from "../../models/Topic";
import SSOUser from "./SSOUser";
import UserTitle from "./SSOUser/UserTitle";
import { getCurrentUserSsoId } from "../../state/selectors/general.selectors";

function CommentItem({
  topic,
  sourceEntity,
  subType,
  subTypeId,
  comment,
  handleReply,
  slug,
  ssoId,
}) {
  const { deleteComment } = apiClient;
  const { createdAt, canEdit, canDelete } = comment || {};
  const { user, id } = comment || {};
  const [isEdit, setEdit] = useState(false);
  const [isDelete, setDelete] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const deleteRef = useRef(null);
  const isInactive = isPortal() || !isFullNamePublic() || !isUserLoggedIn();
  const isMine = ssoId === comment?.user?.ssoId;
  const { type, id: typeId } = sourceEntity || {};

  const handleError = (e) => () => {
    setLoading(false);
    AddToast(e);
    return of(null);
  };

  const getThreadComments = () => {
    const { threadMap } = store.getState();
    return (threadMap.get(slug) || new Topic({})).comments;
  };

  const updateStoreComments = (comments) => {
    const { threadMap: current } = store.getState();
    const newTopic = new Topic({ comments, slug, isFetched: true });
    const threadMap = new Map(current).set(slug, newTopic);
    store.dispatch({ type: "SET_THREAD_MAP", threadMap });
  };

  const onDelete = () => {
    const current = getThreadComments();
    const newComments = current.filter((c) => c !== comment);
    updateStoreComments(newComments);
  };

  const handleDelete = () => {
    const destroy$ = new Subject();

    of(null)
      .pipe(
        tap(() => setLoading(true)),
        switchMap(() => deleteComment(type, typeId, subType, subTypeId, id)),
        tap(() => setLoading(false)),
        tap(onDelete),
        catchError(handleError("Error while deleting...")),
        takeUntil(destroy$)
      )
      .subscribe({});

    return () => destroy$.next();
  };

  if (!comment) return null;

  return (
    <>
      <div className="tw-relative anim-fade-in anim-duration-400">
        <div className="tw-m tw-rounded-lg tw-overflow-hidden tw-relative">
          <div className="tw-p-2 tw-bg-blueGray-100">
            {isEdit ? (
              <CommentForm
                topic={topic}
                className="tw-p-2"
                comment={comment}
                slug={slug}
                sourceEntity={sourceEntity}
                subType={subType}
                subTypeId={subTypeId}
                onSuccess={() => setEdit(false)}
              />
            ) : (
              <>
                <div className="tw-flex tw-items-center">
                  <SSOUser
                    ssoId={user.ssoId}
                    defaultName={user.fullName}
                    UserRenderer={UserTitle}
                  />
                  <div className="tw-mx-2 tw-flex-shrink-0">&bull;</div>
                  <TimeSince date={createdAt} />
                </div>

                <div className="tw-whitespace-pre-wrap comment-content tw-break-words tw-py-2">
                  <Linkify
                    text={comment.htmlComment}
                    isHtml={true}
                    conf={conf}
                  />
                </div>
              </>
            )}
          </div>

          {isLoading ? (
            <div className="tw-absolute tw-inset-0 loading-element tw-rounded-lg tw-opacity-30" />
          ) : null}
        </div>

        <div className="tw-flex tw-pl-6 tw-text-blueGray-400 tw-gap-5 tw-text-xs tw-items-center">
          <LikeButton
            sourceEntity={sourceEntity}
            subType={subType}
            subTypeId={subTypeId}
            object="comment"
            disableForSsoId={user.ssoId}
            slug={comment.slug}
          />

          {isMine || isInactive ? null : (
            <Btn onSubmit={() => handleReply(`@${comment?.user?.screenName}`)}>
              Reply
            </Btn>
          )}

          {!isEdit && canEdit && !isPortal() ? (
            <Btn onSubmit={() => setEdit(!isEdit)}>Edit</Btn>
          ) : null}

          {isEdit ? <Btn onSubmit={() => setEdit(false)}>Cancel</Btn> : null}

          {canDelete && !isPortal() ? (
            <div ref={deleteRef}>
              <Btn onSubmit={() => setDelete(true)}>Delete</Btn>
            </div>
          ) : null}
        </div>
      </div>

      {isDelete ? (
        <ImperativeTooltip
          anchor={deleteRef.current}
          onClose={() => setDelete(false)}
          ContentRenderer={() => (
            <div className="tw-text-blueGray-700 tw-p-3 tw-text-sm">
              Are you sure you want to delete this comment?
              <div className="tw-text-right tw-py-2 tw-flex">
                <div className="tw-flex-grow tw-font-semibold" />

                <Btn
                  className="tw-text-red-500 tw-bg-transparent tw-p-1"
                  onSubmit={() => setDelete(false)}
                >
                  cancel
                </Btn>

                <div className="tw-inline-block tw-w-4" />

                <Btn
                  className="tw-px-2 tw-p-1 tw-bg-red-500 tw-text-white"
                  onSubmit={handleDelete}
                >
                  Delete
                </Btn>
              </div>
            </div>
          )}
        />
      ) : null}
    </>
  );
}

CommentItem.propTypes = {
  topic: PropTypes.instanceOf(Topic).isRequired,
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  comment: PropTypes.instanceOf(Comment),
  handleReply: PropTypes.func,
  subType: PropTypes.string,
  subTypeId: PropTypes.string,
  slug: PropTypes.string,
  ssoId: PropTypes.string,
};

CommentItem.defaultProps = {
  sourceEntity: null,
  subType: "",
  subTypeId: "",
  comment: null,
  handleReply: noop,
  slug: "",
  ssoId: null,
};

const mapStateToProps = (state) => {
  return { ssoId: getCurrentUserSsoId(state) };
};

export default connect(mapStateToProps, {})(React.memo(CommentItem));
